"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

type Parsed = {
  prefix: string;
  number: number;
  suffix: string;
  decimals: number;
  hasComma: boolean;
};

function parseValue(input: string): Parsed | null {
  const match = input.match(/^([^\d.-]*)([\d,]*\d(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const prefix = match[1];
  const raw = match[2];
  const suffix = match[3];
  const cleaned = raw.replace(/,/g, "");
  const number = parseFloat(cleaned);
  if (!Number.isFinite(number)) return null;
  const decimals = cleaned.split(".")[1]?.length ?? 0;
  const hasComma = raw.includes(",");
  return { prefix, number, suffix, decimals, hasComma };
}

function formatNumber(value: number, parsed: Parsed): string {
  const fixed = value.toFixed(parsed.decimals);
  if (!parsed.hasComma) return `${parsed.prefix}${fixed}${parsed.suffix}`;
  const [intPart, decPart] = fixed.split(".");
  const withComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const formatted = decPart ? `${withComma}.${decPart}` : withComma;
  return `${parsed.prefix}${formatted}${parsed.suffix}`;
}

const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Counts up from 0 to the final value when the element scrolls into view.
// Writes textContent directly to skip React reconciliation, keeping
// scrolling smooth with many counters animating at once.
export default function AnimatedNumber({
  value,
  duration = 1500,
}: {
  value: string | undefined;
  duration?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const doneRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useIsoLayoutEffect(() => {
    if (doneRef.current || !value) return;
    const node = ref.current;
    if (!node) return;

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      doneRef.current = true;
      return;
    }

    const parsed = parseValue(value);
    if (!parsed || parsed.number === 0) {
      doneRef.current = true;
      return;
    }

    // Reset to 0 before paint so above-the-fold counters don't flash the
    // final number first.
    node.textContent = formatNumber(0, parsed);

    const runAnimation = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        if (node) {
          node.textContent = formatNumber(
            t < 1 ? parsed.number * eased : parsed.number,
            parsed,
          );
        }
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const rect = node.getBoundingClientRect();
    const inViewport =
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0;
    if (inViewport) {
      runAnimation();
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          obs.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  return <span ref={ref}>{value}</span>;
}
